import { useState, useCallback, useMemo } from 'react';

/**
 * Field types supported by the validation hook
 */
export type FieldType =
  | 'text'
  | 'email'
  | 'phone'
  | 'select'
  | 'date'
  | 'time';

/**
 * Configuration for a single field
 */
export interface FieldConfig {
  value: string;
  type: FieldType;
  required?: boolean;
  label?: string;
}

/**
 * Fields configuration object - maps field names to their configs
 */
export type FieldsConfig = Record<string, FieldConfig>;

/**
 * Result returned by the useFormValidation hook
 */
export interface ValidationResult {
  errors: Record<string, string>;
  isValid: boolean;
  validate: () => boolean;
  validateSingleField: (fieldName: string) => boolean;
  getError: (fieldName: string) => string | undefined;
  setError: (fieldName: string, message: string) => void;
  clearError: (fieldName: string) => void;
  clearAllErrors: () => void;
}

/**
 * Validates a single field based on its type using switch-case
 * @param fieldName - Name of the field
 * @param config - Field configuration
 * @returns Error message or undefined if valid
 */
const validateField = (
  fieldName: string,
  config: FieldConfig
): string | undefined => {
  const label = config.label || fieldName;

  switch (config.type) {
    case 'text':
      // Required validation for text fields
      if (config.required && !config.value.trim()) {
        return `${label} is required`;
      }
      break;

    case 'email':
      // Required validation for email fields
      if (config.required && !config.value.trim()) {
        return `${label} is required`;
      }
      // Email format validation (only if value exists)
      if (
        config.value.trim() &&
        !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(config.value)
      ) {
        return 'Invalid email format';
      }
      break;

    case 'phone':
      // Required validation for phone fields
      if (config.required && !config.value.trim()) {
        return `${label} is required`;
      }
      // Phone format validation - 9 digits (only if value exists)
      if (config.value.trim() && !/^\d{9}$/.test(config.value)) {
        return 'Mobile Number must be 9 digits';
      }
      break;

    case 'select':
      // Required validation for select fields
      if (config.required && !config.value) {
        return `${label} is required`;
      }
      break;

    case 'date':
      // <input type="date"> yields YYYY-MM-DD (or '' when cleared)
      if (config.required && !config.value.trim()) {
        return `${label} is required`;
      }
      break;

    case 'time':
      // <input type="time"> yields HH:MM (or '' when cleared)
      if (config.required && !config.value.trim()) {
        return `${label} is required`;
      }
      break;

    default:
      // Unknown field type - no validation
      break;
  }

  return undefined;
};

/**
 * Custom hook for form validation using switch-case based on field type
 *
 * @param fields - Object mapping field names to their configurations
 * @returns ValidationResult with errors, isValid, and helper methods
 *
 * @example
 * ```tsx
 * const { errors, isValid, validate, getError, clearError } = useFormValidation({
 *   firstName: { value: firstName, type: 'text', required: true, label: 'First Name' },
 *   email: { value: email, type: 'email', required: true, label: 'Email' },
 *   phone: { value: phone, type: 'phone', required: true, label: 'Mobile Number' },
 *   dealer: { value: selectedDealer, type: 'select', required: true, label: 'Dealer' },
 * });
 * ```
 */
export const useFormValidation = (fields: FieldsConfig): ValidationResult => {
  const [errors, setErrors] = useState<Record<string, string>>({});

  /**
   * Validates all fields and sets errors
   * @returns true if all fields are valid, false otherwise
   */
  const validate = useCallback((): boolean => {
    const newErrors: Record<string, string> = {};

    console.log('[useFormValidation] validate() called with fields:', fields);

    for (const [fieldName, config] of Object.entries(fields)) {
      const error = validateField(fieldName, config);
      if (error) {
        newErrors[fieldName] = error;
        console.log(`[useFormValidation] Field "${fieldName}" error:`, error);
      }
    }

    console.log(
      '[useFormValidation] Validation result - errors:',
      newErrors,
      'isValid:',
      Object.keys(newErrors).length === 0
    );

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [fields]);

  /**
   * Computes validity without setting errors (for enabling/disabling submit button)
   */
  const isValid = useMemo((): boolean => {
    for (const [fieldName, config] of Object.entries(fields)) {
      const error = validateField(fieldName, config);
      if (error) {
        return false;
      }
    }
    return true;
  }, [fields]);

  /**
   * Validates a single field and sets its error (for blur validation)
   * @param fieldName - Name of the field to validate
   * @returns true if field is valid, false otherwise
   */
  const validateSingleField = useCallback(
    (fieldName: string): boolean => {
      const config = fields[fieldName];
      if (!config) return true;

      const error = validateField(fieldName, config);
      if (error) {
        setErrors((prev) => ({ ...prev, [fieldName]: error }));
        return false;
      } else {
        // Clear error if field is now valid
        setErrors((prev) => {
          const { [fieldName]: _, ...rest } = prev;
          return rest;
        });
        return true;
      }
    },
    [fields]
  );

  /**
   * Gets error message for a specific field
   */
  const getError = useCallback(
    (fieldName: string): string | undefined => {
      return errors[fieldName];
    },
    [errors]
  );

  /**
   * Clears error for a specific field
   */
  const clearError = useCallback((fieldName: string): void => {
    setErrors((prev) => {
      const { [fieldName]: _, ...rest } = prev;
      return rest;
    });
  }, []);

  /**
   * Sets an error for a specific field (for manual error handling like API errors)
   */
  const setError = useCallback((fieldName: string, message: string): void => {
    setErrors((prev) => ({
      ...prev,
      [fieldName]: message,
    }));
  }, []);

  /**
   * Clears all errors
   */
  const clearAllErrors = useCallback((): void => {
    setErrors({});
  }, []);

  return {
    errors,
    isValid,
    validate,
    validateSingleField,
    getError,
    setError,
    clearError,
    clearAllErrors,
  };
};

export default useFormValidation;
